import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  styled,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ArrowDropDown } from '@mui/icons-material';
import useFetchObject from '../../api/useFetchObject';
import useFetchObjects from '../../api/useFetchObjects';
import useUpdate from '../../api/useUpdate';
import { useAuth } from '../../context/AuthContext';

// RTL-aware TextField component with improved styling
const RTLTextField = styled(TextField)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      padding: '10px 14px',
    },
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiOutlinedInput-root': {
      paddingLeft: 14,
      paddingRight: 0,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      textAlign: 'right',
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

// RTL-aware FormControl with improved dropdown styling
const RTLFormControl = styled(FormControl)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
    },
    '& .MuiSelect-select': {
      paddingRight: '32px',
      textAlign: 'right',
      paddingLeft: '32px',
    },
    '& .MuiSelect-icon': {
      right: 'auto',
      left: 7,
      pointerEvents: 'none',
      color: theme.palette.action.active,
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

// RTL-aware Button component
const RTLButton = styled(Button)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
  }),
}));

const DeputyMinistryEdit = () => {
  const { t, i18n } = useTranslation(['deputy-ministries', 'common', 'validation']);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = user?.token;
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  // Validation state
  const [errors, setErrors] = useState({});
  
  // Fetch deputy ministry data
  const { 
    data: deputyMinistryData, 
    isLoading: isLoadingDeputyMinistry,
    isError: isDeputyMinistryError,
    error: deputyMinistryError
  } = useFetchObject('deputy-ministries', `deputy-ministries/${id}`, id, token);

  // Update mutation hook
  const { 
    handleUpdate, 
    loading: isUpdating, 
    success: updateSuccess 
  } = useUpdate(
    'deputy-ministries',
    token,
    '/deputy-ministries',
    t('update_success'),
    t('update_error')
  );

  // Set form data when deputy ministry data is loaded
  useEffect(() => {
    if (deputyMinistryData) {
      // Check if data is nested (API standard response format)
      const ministryData = deputyMinistryData.data || deputyMinistryData;
      
      setFormData({
        name: ministryData.name || '',
        description: ministryData.description || '',
      });
    }
  }, [deputyMinistryData]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation:required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Create a copy of the form data
    const submissionData = { ...formData };
    
    // Submit form data
    handleUpdate(id, submissionData);
  };

  // Show loading state
  if (isLoadingDeputyMinistry) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (isDeputyMinistryError) {
    return (
      <Box>
        <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
          {t('not_found')}
        </Typography>
        <Alert severity="error" sx={{ mb: 3 }}>
          {deputyMinistryError?.message || t('error_occurred')}
        </Alert>
        <RTLButton
          component={RouterLink}
          to="/deputy-ministries"
          startIcon={!isRtl && <ArrowBackIcon />}
          endIcon={isRtl && <ArrowBackIcon sx={{ transform: 'scaleX(-1)' }} />}
          isRtl={isRtl}
        >
          {t('common:back_to_list')}
        </RTLButton>
      </Box>
    );
  }

  return (
    <Box 
      component="form" 
      onSubmit={handleSubmit} 
      dir={isRtl ? 'rtl' : 'ltr'}
      sx={{ textAlign: isRtl ? 'right' : 'left', width: '100%' }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 3, 
        flexDirection: isRtl ? 'row-reverse' : 'row',
        width: '100%'
      }}>
        <RTLButton
          component={RouterLink}
          to="/deputy-ministries"
          startIcon={!isRtl && <ArrowBackIcon />}
          endIcon={isRtl && <ArrowBackIcon sx={{ transform: 'scaleX(-1)' }} />}
          sx={{ 
            mr: isRtl ? 0 : 2, 
            ml: isRtl ? 2 : 0,
            whiteSpace: 'nowrap'
          }}
          isRtl={isRtl}
        >
          {t('common:back')}
        </RTLButton>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            flexGrow: 1,
            textAlign: isRtl ? 'right' : 'left',
            fontWeight: 'bold',
          }}
        >
          {t('edit_title')}
        </Typography>
      </Box>

      <Card sx={{ mb: 4, width: '100%' }}>
        <CardContent>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ 
              textAlign: isRtl ? 'right' : 'left',
              fontWeight: 600,
            }}
          >
            {t('basic_info')}
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <RTLTextField
                fullWidth
                name="name"
                label={t('name')}
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                required
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
                FormHelperTextProps={{
                  style: { textAlign: isRtl ? 'right' : 'left' }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <RTLTextField
                fullWidth
                name="description"
                label={t('description')}
                value={formData.description || ''}
                onChange={handleChange}
                multiline
                rows={3}
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
                FormHelperTextProps={{
                  style: { textAlign: isRtl ? 'right' : 'left' }
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      <Box sx={{ 
        display: 'flex', 
        justifyContent: isRtl ? 'flex-start' : 'flex-end', 
        gap: 2,
        width: '100%' 
      }}>
        <RTLButton
          variant="outlined"
          component={RouterLink}
          to="/deputy-ministries"
          isRtl={isRtl}
        >
          {t('common:cancel')}
        </RTLButton>
        <RTLButton
          variant="contained"
          type="submit"
          disabled={isUpdating}
          isRtl={isRtl}
        >
          {isUpdating ? (
            <CircularProgress size={24} />
          ) : (
            t('common:save')
          )}
        </RTLButton>
      </Box>
    </Box>
  );
};

export default DeputyMinistryEdit; 